<template>
    <div v-if="isLoaded" class="events">
        <h4>
            {{ $t('reports.reportForm.events') }}
            <span v-if="locationReport.length > 0" class="events-count">
                ({{ more ? $t('reports.reportForm.count') : locationReport.length }})
            </span>
        </h4>
        <div v-if="Object.keys(reportData.selectedEvent).length !== 0" class="event-title">
            <span>{{ $t('reports.reportForm.event') }}:</span>
            <span>
                {{ reportData.selectedEvent.types }}
                ({{ reportData.selectedEvent.location }})
                {{ getFormatedStartTime(reportData.selectedEvent.start) }}
                ({{
                    timeDifference(
                        reportData.selectedEvent.start,
                        reportData.selectedEvent.end,
                        reportData.selectedEvent.incomplete
                    )
                }})
            </span>
        </div>
        <div v-if="locationReport.length > 0">
            <table class="events-table">
                <thead>
                    <tr>
                        <th>{{ $t('reports.labels.eventType') }}</th>
                        <th>{{ $t('common.location') }}</th>
                        <th>{{ $t('reports.reportForm.start') }}</th>
                        <th>{{ $t('reports.reportForm.duration') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <simplebar class="with-border-track">
                        <tr v-for="call in locationReport" :key="call.calltrace_id">
                            <td class="events-radio">
                                <input
                                    :id="'call-' + call.calltrace_id"
                                    v-model="selectedEvent.calltrace_id"
                                    type="radio"
                                    name="event"
                                    :value="call.calltrace_id"
                                    @click="setCall(call)"
                                />
                                <label :for="'call-' + call.calltrace_id">
                                    <div class="radio-icon"></div>
                                    {{ parseCallTypes(call.categories).join(', ') }}
                                </label>
                            </td>
                            <td>{{ call.location }}</td>
                            <td>{{ call.startTimeFormatted }}</td>
                            <td>{{ call.timeDifferenceFormatted }}</td>
                        </tr>
                    </simplebar>
                </tbody>
            </table>
            <button v-if="more" class="table-more" @click.prevent="loadLocationReport(true)">
                {{ $t('reports.reportForm.more') }}
            </button>
        </div>
        <div v-else class="ascom-noevents">{{ $t('reports.reportForm.noevents') }}</div>
    </div>
</template>

<script type="text/babel">
import { mapState } from 'vuex';
import isEmpty from 'lodash-es/isEmpty';
import reportingUtils from '../../common/reportingUtils';
import { resourceLocationReport } from '../../../api/resources';
import { getCancelToken } from '../../../api';
import simplebar from 'simplebar-vue';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { DEF_DATE_FORMAT } from '../../../constants';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { getFullDateFormat } from '../../common/helpers';

export default {
    components: {
        simplebar,
    },

    mixins: [reportingUtils],

    props: {
        value: {
            type: Object,
            required: true,
        },

        reportData: {
            type: Object,
            required: true,
        },

        mode: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            locationReport: [],
            limit: 101,
            offset: 0,
            more: true,
            isLoaded: false,
            selectedEvent: {},
            isWatch: true,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState({ aAreas: 'assignmentArea' }),

        /**
         * Forms a list of locations obtained from the form filters. If no filter location is selected, it
         * returns all available locations.
         * @return {array} of locations
         */
        locationFilter() {
            let locations = [];
            let locs = this.reportData.locations;
            if (locs.length > 0) {
                locations = locs.map(l => l.name);
            } else {
                this.aAreas.forEach(aa => {
                    if (this.reportData.aa.length === 0) {
                        aa.locations.forEach(l => locations.push(l.name));
                    } else {
                        this.reportData.aa.forEach(a => {
                            if (aa.id === a.value) {
                                aa.locations.forEach(l => locations.push(l.name));
                            }
                        });
                    }
                });
            }

            return locations;
        },

        /**
         * Forms a list of call types id's obtained from the form filters. If no filter call types is selected, it
         * returns all available id's (from 1 to 11).
         * @return {array} of call type id's
         */
        callTypesFilter() {
            return this.reportData.callTypes.length > 0
                ? this.reportData.callTypes.map(c => c.value)
                : Array(11)
                      .fill()
                      .map((x, i) => i + 1);
        },

        /**
         * Forms an object with parameters for body of ajax call.
         * @return {object}
         */
        locationParameters() {
            let parameters = {
                locations: this.locationFilter,
                call_types: this.callTypesFilter,
                offset: this.offset,
                limit: this.limit,
            };

            if (this.reportData.exportDate.from.date) {
                let date =
                    this.reportData.exportDate.from.date instanceof Date
                        ? this.reportData.exportDate.from.date
                        : new Date(parseInt(this.reportData.exportDate.from.date));
                parameters.time_from = format(date, DEF_DATE_FORMAT);
            }

            if (this.reportData.exportDate.to.date) {
                let date =
                    this.reportData.exportDate.to.date instanceof Date
                        ? this.reportData.exportDate.to.date
                        : new Date(parseInt(this.reportData.exportDate.to.date));
                parameters.time_to = format(date, DEF_DATE_FORMAT);
            }
            return parameters;
        },
    },

    watch: {
        'locationParameters.locations'(val, oldVal) {
            this.checkOnValidAndUpdate(val, oldVal);
        },

        'locationParameters.call_types'(val, oldVal) {
            this.checkOnValidAndUpdate(val, oldVal);
        },

        'locationParameters.time_from'(val, oldVal) {
            this.checkOnValidAndUpdate(val, oldVal, true);
        },

        'locationParameters.time_to'(val, oldVal) {
            this.checkOnValidAndUpdate(val, oldVal, true);
        },
    },

    created() {
        this.$watch('$route', () => {
            this.updateWatchedTable();
            this.isWatch = false;
        });
        return this.loadLocationReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        getFormatedStartTime(time) {
            return format(parseISO(time), getFullDateFormat());
        },

        /**
         * Fetch data with report parameters
         */
        loadLocationReport(showMorePressed) {
            let data = this.locationParameters;
            this.isWatch = true;

            return resourceLocationReport({
                method: 'post',
                data,
                cancelToken: this.cancelToken.token,
            })
                .then(response => {
                    let data = response.data;
                    this.offset += 100;
                    let newData = [];
                    if (data.length % 101 !== 0) {
                        this.more = false;
                        newData = data;
                    } else {
                        this.more = true;
                        newData = data.splice(0, data.length - 1);
                    }
                    let newDataMapped = newData.map(call => ({
                        ...call,
                        startTimeFormatted: this.formatDateString(call.start_time),
                        timeDifferenceFormatted: this.timeDifference(
                            call.start_time,
                            call.end_time,
                            call.incomplete
                        ),
                    }));

                    if (!showMorePressed) {
                        // Here if filter parameters changed
                        // Forget old call traces
                        this.locationReport = newDataMapped;
                    } else {
                        // Here if <Show More> button pressed (filter parameters not changed)
                        this.locationReport = this.locationReport.concat(
                            newDataMapped
                                // Filter out already present call traces
                                .filter(
                                    newCall =>
                                        !this.locationReport.find(
                                            existingCall => newCall.calltrace_id === existingCall.calltrace_id
                                        )
                                )
                        );
                    }
                })
                .catch(() => {
                    this.locationReport = [];
                })
                .then(() => {
                    this.isLoaded = true;
                    this.setEvent();
                });
        },

        /**
         * Sets the selected call into selectedEvent object with needed parameters for custom reports and
         * calltrace_id for getting information for Ascom Event report and emit an event to update reportData.
         * @param {object} call
         */
        setCall(call) {
            this.selectedEvent = {
                types: this.parseCallTypes(call.categories).join(', '),
                location: call.location,
                start: call.start_time,
                end: call.end_time,
                calltrace_id: call.calltrace_id,
                incomplete: call.incomplete,
            };
            this.$emit('input', this.selectedEvent);
        },

        /**
         * Set selected event, dependent on value or location report data
         */
        setEvent() {
            if (this.locationReport.length > 0) {
                if (isEmpty(this.value)) {
                    this.setCall(this.locationReport[0]);
                } else {
                    this.selectedEvent = this.value;
                }
            } else {
                if (this.mode === 'add') {
                    this.selectedEvent = {};
                    this.$emit('input', {});
                }
            }
        },
        /**
         * Loads new information into the table.
         */
        updateTable() {
            this.isLoaded = false;
            this.offset = 0;
            this.more = true;
            if (!isEmpty(this.reportData.selectedEvent)) {
                this.$emit('input', {});
            }
            this.loadLocationReport();
        },

        updateCustomReport() {
            this.isLoaded = false;
            this.offset = 0;
            this.loadLocationReport().then(() => (this.selectedEvent = this.reportData.selectedEvent));
        },

        updateWatchedTable() {
            this.$nextTick(() => {
                if (this.mode === 'add') {
                    this.updateTable();
                } else {
                    this.updateCustomReport();
                }
            });
        },

        /**
         * Check validation and if validation is correct - send ajax
         * @param {string|array} val
         * @param {string|array} oldVal
         * @param {boolean} time
         */
        checkOnValidAndUpdate(val, oldVal, time = false) {
            this.$nextTick(() => {
                if (this.isWatch && !this.reportData.exportDate.invalid) {
                    if (time && val !== oldVal) {
                        this.updateWatchedTable();
                    } else if (val.length !== oldVal.length) {
                        this.updateWatchedTable();
                    }
                }
            });
        },

        /**
         * Get difference between two dates in seconds
         * @param start time
         * @param end time
         * @param incomplete
         * @return string in HH:MM:SS format
         */
        timeDifference(start, end, incomplete) {
            if (incomplete) {
                return this.$t('reports.unknown');
            }
            if (!end) {
                return this.$t('reports.ongoing');
            }

            let seconds = differenceInSeconds(parseISO(end), parseISO(start));
            // Break s into hours:minutes:seconds
            let hours = Math.floor(seconds / 3600);
            seconds = seconds % 3600;
            let minutes = Math.floor(seconds / 60);
            seconds = seconds % 60;

            return [hours, minutes, seconds].map(item => String(item).padStart(2, '0')).join(':');
        },
    },
};
</script>
